import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi"; // BackEnd API
import {
  fnGetWhLocLotCtPltInvDtl,
  fnGetWhSnInv,
  fnGetWhPjtSnInv,
} from "@shared/utils/InvUtils";
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import * as converter from "@shared/utils/ValueConverterUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";

interface Props {
  Menuprops: menuInfo;
}

const SN_PRD_WRK_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);

  const searchRef = useRef<any>(null);
  const { userInfo, config } = global(); // Login user 정보

  // 26.11.04 kdy MAS0002(프로젝트 사용유무). 이 화면은 프로젝트가 헤더(grid1)에 있어
  // 재고리스트(grid3) 필터에만 헤더의 cd_pjt 조건을 더한다(재고 조회·캐시는 품목+창고 그대로).
  const mas0002 = config?.MAS0002?.dc_ctrl ?? "N"; // 프로젝트 사용유무 Y/N

  // grid3 필터 조건에 붙일 프로젝트 조건. 값을 안 넘기면 헤더 포커스 행의 cd_pjt, ""를 넘기면 필터 해제
  const pjtFilter = (cd_pjt?: any) =>
    mas0002 === "Y"
      ? {
          cd_pjt:
            cd_pjt ??
            getGridCurrent("grid1").focusedRowNode()?.data?.cd_pjt ??
            "",
        }
      : {};

  // SN재고 조회. 프로젝트 사용 시 프로젝트 SN재고(헤더 cd_pjt 기준, 결과에 cd_pjt 포함), 아니면 기존 SN재고
  const fetchSnInv = async (params: any) => {
    if (mas0002 !== "Y") return fnGetWhSnInv(params);
    return fnGetWhPjtSnInv(params);
  };
  //#endregion

  // 재고리스트
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid4: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (modifiedRowsL.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_prd_wrk_reg_004_01",
        params: searchDataToUse,
      };

      setLotSnData([]);

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: [],
        grid4: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
    const isInsertRow = insertRows.some(
      (row: any) =>
        row.no_line === focusedRowNodeL.data.no_line &&
        row.no_io === focusedRowNodeL.data.no_io
    );

    if (isInsertRow) {
      resetRowData();
    } else {
      getGridCurrent("grid4").deleteAllRows();
    }

    getGridCurrent("grid2").deleteSelectedRows();

    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
        ...pjtFilter(""),
      });
    }
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid2").stopEditing();

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const conslChanges = getGridCurrent("grid2").getChange();
      const qtiolotsnChanges = getGridCurrent("grid4").getChange();

      if (
        conslChanges.modifiedRowcount === 0 &&
        qtiolotsnChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 요청 데이터 구성
      const SaveData = {
        consl: conslChanges,
        qtio_lotsn: qtiolotsnChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PRD_WRK_REG_004_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();

      setLotSnData([]);
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onFocusedRowChangedH = (event: any) => {
    getGridCurrent("grid2").applyFilters({
      no_run: event.no_run,
      no_line: {
        operator: "OR",
        conditions: [
          { type: "equals", filter: event.no_line },
          { type: "equals", filter: 0 },
        ],
      },
    });

    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      getGridCurrent("grid2").selectAndFocusRow(0);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid2").clearFocus();
        getGridCurrent("grid2").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid4").applyFilters({
          no_io: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      let cd_wh_last = "";
      let nm_wh_last = "";
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount !== 0) {
        const lastNode = getGridCurrent("grid2").getRowNodeAtIndex(
          rowCount - 1
        );

        if (lastNode != null) {
          cd_wh_last = lastNode.data.cd_wh;
          nm_wh_last = lastNode.data.nm_wh;
        }
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        no_line: maxNoLine + 1,
        no_run: focusedRowNodeH.data?.no_run,
        no_line_run: focusedRowNodeH.data?.no_line_run,
        qt: 0,
        qt_w: 0,
        qt_b: 0,
        no_line_bill: 0,
        cd_wh: cd_wh_last,
        nm_wh: nm_wh_last,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    switch (colId) {
      case "cd_wh":
        const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
        if (rowCount === 1) return;
        if (!rowData.cd_wh) return;

        let apply = false;
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          )
            apply = true;
        });
        if (!apply) return;

        const confirm = await showDialog(
          "confirm",
          "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
        );

        if (confirm) {
          const visibleIdx = rowNode.rowIndex;

          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            if (
              node.data.cd_wh === "" ||
              node.data.cd_wh === null ||
              node.data.cd_wh === undefined
            ) {
              node.setDataValue("cd_wh", rowData.cd_wh);
              node.setDataValue("nm_wh", rowData.nm_wh);
            }
          });
          if (visibleIdx) {
            getGridCurrent("grid2").selectAndFocusRow(visibleIdx);
          }
        }
        break;
      // 품목바뀌면 수량 초기화
      case "cd_matl":
        rowData.qt = 0;
        rowData.qt_w = 0;
        rowData.qt_b = 0;

        if (converter.getString(rowData.cd_matl) !== "") {
          const params = {
            id: "wobill",
            params: {
              cd_firm: userInfo.cd_firm,
              no_wo: focusedRowNodeH.data.no_wo,
            },
          };

          const woBill = await fnGetTableById(params);
          const wobillLine = woBill.find(
            (bill: any) => bill.cd_matl === rowData.cd_matl
          );

          if (wobillLine) {
            rowData.no_line_bill = wobillLine.no_line;
          } else {
            rowData.no_line_bill = 0;
          }
        }
        break;
      case "qt_w":
      case "qt_b":
        rowNode.setDataValue("qt", rowData.qt_w + rowData.qt_b, "noEvent");
        break;
    }

    switch (colId) {
      case "cd_matl":
      case "cd_wh":
      case "nm_wh":
        if (
          rowData.cd_matl !== "" &&
          rowData.cd_matl !== null &&
          rowData.cd_matl !== undefined &&
          rowData.cd_wh !== "" &&
          rowData.cd_wh !== null &&
          rowData.cd_wh !== undefined
        ) {
          // 품목이나 창고가 바뀌면 꼬일 수 있으므로 초기화
          rowData.qt = 0;
          rowData.qt_w = 0;
          rowData.qt_b = 0;
          resetRowData();

          const data = {
            cd_biz: focusedRowNodeH.data.cd_biz,
            cd_item: rowData.cd_matl,
            cd_wh: rowData.cd_wh,
            cd_loc: "",
            cd_pjt: focusedRowNodeH.data.cd_pjt ?? "", // 프로젝트 SN재고 조회용(MAS0002=Y)
          };

          if (
            !lotSnDataRef.current.some(
              (item) =>
                item.cd_item === rowData.cd_matl && item.cd_wh === rowData.cd_wh
            )
          ) {
            switch (rowData.fg_unit) {
              case "LOT":
                const searchLotData = await fnGetWhLocLotCtPltInvDtl(data);
                const lotData = searchLotData.map((item: any) => {
                  const { no_lot, ...rest } = item; // no_lot만 추출

                  return {
                    ...rest,
                    no_barcode: no_lot, // 필드명 바꾸기
                    fg_unit: "LOT", // LOT/SN구분
                    no_src: item.no_io, // 재고전표
                    no_line_src: item.no_line, // 재고전표항번
                    no_line_lot_src: item.no_line_lot, // 재고전표항번디테일
                    qt_isu: 0, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some(
                    (item) =>
                      item.cd_item === rowData.cd_matl &&
                      item.cd_wh === rowData.cd_wh
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...lotData];
                });
                break;
              case "SN":
                const searchSnData = await fetchSnInv(data);
                const snData = searchSnData.map((item: any) => {
                  const { no_sn, ...rest } = item;

                  return {
                    ...rest,
                    no_barcode: no_sn, // 필드명 바꾸기
                    fg_unit: "SN", // LOT/SN구분
                    no_src: item.no_io, // 재고전표
                    no_line_src: item.no_line, // 재고전표항번
                    no_line_lot_src: 0, // SN재고는 무의미
                    qt_isu: 1, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some(
                    (item) =>
                      item.cd_item === rowData.cd_matl &&
                      item.cd_wh === rowData.cd_wh
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...snData];
                });
                break;
            }
          }
          // 재고리스트 필터돌게
          getGridCurrent("grid2").clearFocus();
          getGridCurrent("grid2").selectAndFocusRow(rowNode.rowIndex);
        } else {
          resetRowData();
        }
        break;
    }
  };

  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      if (selectedRow.state === "insert") {
        getGridCurrent("grid3").applyFilters({
          cd_item: selectedRow.cd_matl,
          cd_wh: selectedRow.cd_wh,
          ...pjtFilter(),
          qt_inv: "notZero",
        });
      } else {
        getGridCurrent("grid3").applyFilters({
          cd_item: "",
          cd_wh: "",
          ...pjtFilter(""),
        });
      }

      getGridCurrent("grid4").applyFilters({
        no_io: selectedRow.no_run,
        no_line: selectedRow.no_line,
      });
    } else {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
        ...pjtFilter(""),
      });

      getGridCurrent("grid4").applyFilters({
        no_io: "",
        no_line: "",
      });
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region GridLotSn Events
  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0);
      }
    }
  };

  const onRowDataUpdatedLotSn = () => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    // 신규일 때만 바코드 컴포넌트 포커스 타게
    if (focusedRowNodeL) {
      if (focusedRowNodeL.data.state === "insert") {
        txtBarcodeRef.current.focus();
      }
    }
  };
  //#endregion

  //#region GridD Events
  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
        if (focusedRowNodeL.data.state !== "insert") {
          await showDialog("alter", "신규건만 삭제할 수 있습니다.");
          return;
        }

        const focusedRowNodeD = getGridCurrent("grid4").focusedRowNode();

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (
            node.data.cd_item === focusedRowNodeD.data?.cd_item &&
            node.data.cd_wh === focusedRowNodeD.data?.cd_wh &&
            node.data.no_barcode === focusedRowNodeD.data?.no_barcode &&
            node.data.no_src === focusedRowNodeD.data?.no_src &&
            node.data.no_line_src === focusedRowNodeD.data?.no_line_src &&
            node.data.no_line_lot_src === focusedRowNodeD.data?.no_line_lot_src
          ) {
            node.setDataValue(
              "qt_inv",
              node.data.qt_inv + focusedRowNodeD.data?.qt_good
            );

            if (node.data.fg_unit === "SN") {
              node.setDataValue("qt_isu", 1);
            }

            focusedRowNodeL.setDataValue(
              "qt_w",
              focusedRowNodeL.data?.qt_w - focusedRowNodeD.data?.qt_good
            );
            return;
          }
        });
        getGridCurrent("grid4").deleteSelectedRows();
        getGridCurrent("grid3").applyFilters({
          cd_item: focusedRowNodeL.data.cd_matl,
          cd_wh: focusedRowNodeL.data.cd_wh,
          ...pjtFilter(),
          qt_inv: "notZero",
        });
        break;
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_matl,
      cd_wh: focusedRowNodeL.data.cd_wh,
      ...pjtFilter(),
      qt_inv: "notZero",
    });

    const barcode = e;

    if (barcode === "" || barcode === null || barcode === undefined) {
      return;
    }

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      selectInv("S", barcode);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_matl,
          cd_wh: focusedRowNodeL.data.cd_wh,
          ...pjtFilter(),
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };
  //#endregion

  //#region Method
  const selectInv = (id: string, barcode: string) => {
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    if (getGridCurrent("grid4").getDisplayedRowCount() > 0) {
      showDialog("alter", "한 건씩만 등록 가능합니다.");
      return false;
    }

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let noBarcode = "";
    let noOrigin = "";
    let noSrc = "";
    let noLineSrc = 0,
      noLineLotSrc = 0,
      noLineOrigin = 0,
      noLineLotOrigin = 0;
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        } else if (selectedLotSn.length > 1) {
          showDialog("alter", "한 건씩만 등록 가능합니다.");
          return false;
        }

        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.no_src}_${row.no_line_src}_${row.no_line_lot_src}`;
          // 사용자가 그리드3에 입력한 '출고예정수량(qt_isu)'을 가져옴
          selectedMap.set(key, Number(row.qt_isu) || 0);
        });
        break;
      case "S": // 스캔
        qtRemain = -1;
        break;
    }

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.no_src}_${row.no_line_src}_${row.no_line_lot_src}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
      }

      if (qtIsu <= 0) return;

      noBarcode = node.data.no_barcode;
      noSrc = node.data.no_src;
      noLineSrc = node.data.no_line_src;
      noLineLotSrc = node.data.no_line_lot_src;
      noOrigin = node.data.no_origin;
      noLineOrigin = node.data.no_line_origin;
      noLineLotOrigin = node.data.no_line_lot_origin;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        no_io: focusedRowNodeH.data?.no_run,
        no_line: focusedRowNodeL.data?.no_line,
        dt_io: focusedRowNodeH.data?.dt_run,
        tp_slip: "3",
        fg_slip: "2",
        fg_io: "1",
        fg_money: "1",
        // cd_io: focusedRowNodeL.data?.cd_io,
        cd_item: focusedRowNodeL.data?.cd_matl,
        cd_wh: focusedRowNodeL.data?.cd_wh,
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_src: node.data.no_src, // 재고전표
        no_line_src: node.data.no_line_src, // 재고전표항번
        no_line_lot_src: node.data.no_line_lot_src, // 재고전표항번디테일
        no_origin: node.data.no_origin, // 재고원입고전표
        no_line_origin: node.data.no_line_origin, // 재고원입고전표항번
        no_line_lot_origin: node.data.no_line_lot_origin, // 재고원입고전표디테일
        cd_pjt: focusedRowNodeH.data?.cd_pjt ?? "", // 헤더 프로젝트
        no_batch: node.data.no_batch, // 26.11.02 추가
      };

      // qtRemain === -1은 요청량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          node4.data.cd_item === focusedRowNodeL.data?.cd_matl &&
          node4.data.cd_wh === focusedRowNodeL.data?.cd_wh &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.no_src === node.data.no_src &&
          node4.data.no_line_src === node.data.no_line_src &&
          node4.data.no_line_lot_src === node.data.no_line_lot_src &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue("qt_w", focusedRowNodeL.data?.qt_w + qtAdd);

    if (focusedRowNodeL.data.fg_unit === "LOT") {
      focusedRowNodeL.setDataValue("no_lot", noBarcode, "noEvent");
      focusedRowNodeL.setDataValue("no_sn", "", "noEvent");
      focusedRowNodeL.setDataValue("no_io", noSrc, "noEvent"); // 재고전표
      focusedRowNodeL.setDataValue("no_line_io", noLineSrc, "noEvent"); // 재고전표항번
      focusedRowNodeL.setDataValue("no_line_lot_io", noLineLotSrc, "noEvent"); // 재고전표항번디테일
      focusedRowNodeL.setDataValue("no_origin", noOrigin, "noEvent"); // 재고원입고전표
      focusedRowNodeL.setDataValue("no_line_origin", noLineOrigin, "noEvent"); // 재고원입고전표항번
      focusedRowNodeL.setDataValue(
        "no_line_lot_origin",
        noLineLotOrigin,
        "noEvent"
      ); // 재고원입고전표디테일
    } else {
      focusedRowNodeL.setDataValue("no_lot", "", "noEvent");
      focusedRowNodeL.setDataValue("no_sn", noBarcode, "noEvent");
      focusedRowNodeL.setDataValue("no_io", noSrc, "noEvent"); // 재고전표
      focusedRowNodeL.setDataValue("no_line_io", noLineSrc, "noEvent"); // 재고전표항번
      focusedRowNodeL.setDataValue("no_line_lot_io", 0, "noEvent"); // SN재고는 무의미
      focusedRowNodeL.setDataValue("no_origin", noOrigin, "noEvent"); // 재고원입고전표
      focusedRowNodeL.setDataValue("no_line_origin", noLineOrigin, "noEvent"); // 재고원입고전표항번
      focusedRowNodeL.setDataValue("no_line_lot_origin", 0, "noEvent"); // SN재고는 무의미
    }

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_matl,
      cd_wh: focusedRowNodeL.data.cd_wh,
      ...pjtFilter(),
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };

  const resetRowData = (): any => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          node4.data.cd_item === node.data?.cd_item &&
          node4.data.cd_wh === node.data?.cd_wh &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue("qt_inv", node.data.qt_inv + node4.data.qt_good);

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();

    getGridCurrent("grid3").applyFilters({
      cd_item: "",
      cd_wh: "",
      ...pjtFilter(""),
    });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onBtnAddClick: onGridLBtnsClick,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: lotSnData,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
          onRowDataUpdated: onRowDataUpdatedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="50%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "실적정보" }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="50">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "투입정보" }}
                events={grids.grid2.events}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
              <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={lotSnData} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    title: "재고리스트",
                    ischeckboxSelection: true,
                  }}
                  events={grids.grid3.events}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <SnSwitch
                        ref={switchRef}
                        labelCheck="검색"
                        labelUncheck="스캔"
                        defaultChecked={false}
                      />
                      <div>
                        <SnBarcodeBox
                          onEnter={onBarcodeEnter}
                          inputRef={txtBarcodeRef}
                        />
                      </div>
                    </div>
                  }
                />
              </SnResizeBar>
              <div className="flex-1 min-h-0">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "single", title: "상세정보" }}
                  events={grids.grid4.events}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
  //#endregion
});

export default SN_PRD_WRK_REG_004;
